/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-11-16 15:58:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Card,
  DataTable,
  SkeletonDisplayText,
  Pagination,
  Icon,
  TextStyle,
  Tooltip,
  TextField
} from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { CaretDownMinor, CaretUpMinor, SearchMinor } from '@shopify/polaris-icons';
import { getUTCDate } from 'src/helpers/date-time-helper';
import { buildQueryKey, findExecutionId } from 'src/helpers/analytics-query-execution-id-helper';
import Filter from 'src/components/containers/analytics-v2/filter';
import {
  QUERY_EXECUTION_KEY,
  TOP_ITEM_IN_DETAIL,
  NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL,
  MAP_SORT_VALUE,
  SORT_VALUE,
  RECOMMENDATION_ANALYTIC_METRIC,
  FEATURES
} from 'src/constants/analytics';
import { formatNumberInThousand } from 'src/helpers';
import * as AnalyticsActions from 'src/stores/actions/analytics-v2-actions';
import * as AnalyticsQueryExecutionIdActions from 'src/stores/actions/analytics-query-execution-id-actions';
import { processHeaderTable } from '../index';
import { onAbortApiAnalytic } from 'src/helpers/analytics-v2-helpers';
import history from 'src/routes/history';

type Props = {
  performActionSurvey: () => void
};
type ComposedProps = Props & WithI18nProps;
type State = {
  perPage: number,
  page: number,
  hasNext: boolean,
  hasPrevious: boolean,
  data: any[],
  totalPage: number,
  directionSort: string
};

const fromDateStateName = 'recommendationAnalyticsFromDate';
const toDateStateName = 'recommendationAnalyticsToDate';
const compareStateName = 'recommendationAnalyticsCompare';

const orderHeaders = [
  'widgetId',
  'totalRevenue',
  'orderCount',
  'itemInOrderPercent',
  'conversionRate',
  'itemInCartPercent',
  'totalView',
  'totalClick',
  'clickRate'
];

// {
//   "widgetId": "productpage-29904219746",
//   "widgetName": "other page",
//   "totalRevenue": 43923173657,
//   "orderCount": 5483053,
//   "itemInOrderPercent": 4.13,
//   "conversionRate": 48.28,
//   "itemInCartPercent": 47.44,
//   "totalView": 77412,
//   "totalClick": 45064,
//   "clickRate": 98.24
// }

const filterWidgetBySearch = (arr, currentKeySearch) => {
  const arrFiltered = arr.filter(widget => {
    if (widget.widgetId?.toLowerCase().includes(currentKeySearch.toLowerCase())) return true;
    return false;
  });
  return arrFiltered;
};
class CollectionPerformanceAnalytics extends Component<ComposedProps, State> {
  state = {
    perPage: NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL,
    hasPrevious: false,
    hasNext: false,
    data: [],
    page: 1,
    totalPage: 0,
    directionSort: 'desc',
    currentKeySearch: '',
    recommendationTopWidgetPerformanceOrdered: this.props.recommendationTopWidgetPerformance,
    highlightId: ''
  };

  handleHighlightWidget = (data) => {
    // case mouse entered table body, then do not highlight widget
    if (this.state.highlightId === -1) {
      return 0;
    }
    const { highlightId = '' } = history?.location?.state || {};
    const highlightIndex = data.map(r => r.widgetId).indexOf(highlightId);

    if (!highlightId || highlightIndex < 0) {
      return 0;
    }

    this.setState({ highlightId })
    const tbody = document.querySelector(".BC-Page__RecommendationAnalytics__Top__Widget tbody");
    const mousemoveFunc = () => {
      this.setState({ highlightId: -1 });
      tbody.removeEventListener('mousemove', mousemoveFunc)
    }
    tbody.addEventListener("mousemove", mousemoveFunc)
    return Math.ceil((highlightIndex + 1) / this.state.perPage);
  }

  componentDidMount() {
    const { recommendationTopWidgetPerformance, fromDate, toDate, compare, i18n } = this.props;
    const { perPage } = this.state;

    const hasNext = _.size(recommendationTopWidgetPerformance) > perPage;
    this.setState({ hasNext });

    if (fromDate && toDate) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(fromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(toDate, 24, 0, 0, 0);
      this.getRecommendationTopWidget(fromDateUTC, toDateUTC, compare);
    }
    processHeaderTable(i18n, 'widget', this.handleSort);
  }

  componentWillReceiveProps(nextProps) {
    const { fromDate, toDate, compare } = this.props;
    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      recommendationTopWidgetPerformance: nextRecommendationTopWidgetPerformance
    } = nextProps;
    const { currentKeySearch } = this.state;
    const dataFilteredAndSorted = filterWidgetBySearch(
      nextRecommendationTopWidgetPerformance,
      currentKeySearch
    );

    let page = this.state.page;
    if (_.isEmpty(this.state.data) && !_.isEmpty(dataFilteredAndSorted)) {
      page = this.handleHighlightWidget(dataFilteredAndSorted) || page;
      this.setState({ page })
    }
    this.setState({
      recommendationTopWidgetPerformanceOrdered: dataFilteredAndSorted
    });

    // Check changing filter
    if (fromDate !== nextFromDate || toDate !== nextToDate || compare !== nextCompare) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(nextFromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(nextToDate, 24, 0, 0, 0);

      // Reset page when change date filter
      this.setState({
        page: page
      });

      onAbortApiAnalytic();
      setTimeout(() => {
        this.getRecommendationTopWidget(fromDateUTC, toDateUTC, nextCompare);
      }, 500);
    }

    if (!_.isEmpty(dataFilteredAndSorted)) {
      const newTotalPage = Math.ceil(
        dataFilteredAndSorted.length / NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL
      );
      this.setState({
        totalPage: newTotalPage
      });
    }

    this.paginateData(page, dataFilteredAndSorted);

    // Update execution ids
    this.processExecutionId_RecommendationTopWidgetPerformance(this.props, nextProps);
  }

  paginateData = (page, data) => {
    this.setState({
      data: data.slice(
        (page - 1) * NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL,
        page * NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL
      )
    });
  };

  buildOptions = () => {
    const { recommendationTopWidgetPerformanceLoading } = this.props;
    const { data, highlightId } = this.state;
    const result = [];
    if (recommendationTopWidgetPerformanceLoading) {
      [...Array(5)].forEach(() =>
        result.push([
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />
        ])
      );
    } else {
      data.forEach(item => {
        const {
          widgetId,
          widgetName,
          totalRevenue,
          orderCount,
          itemInOrderPercent,
          conversionRate,
          itemInCartPercent,
          totalView,
          totalClick,
          clickRate
        } = item;

        result.push([
          <div className={widgetId === highlightId ? 'cell-highlight' : ''}>
            <Tooltip content={widgetName} preferredPosition="above">
              <span>{widgetId}</span>
            </Tooltip>
          </div>,
          <div className="cell-value-table">{formatNumberInThousand(totalRevenue)}</div>,
          <div className="cell-value-table">{formatNumberInThousand(orderCount)}</div>,
          <div className="cell-value-table">{formatNumberInThousand(itemInOrderPercent)}%</div>,
          <div className="cell-value-table">{formatNumberInThousand(conversionRate)}%</div>,
          <div className="cell-value-table">{formatNumberInThousand(itemInCartPercent)}%</div>,
          <div className="cell-value-table">{formatNumberInThousand(totalView)}</div>,
          <div className="cell-value-table">{formatNumberInThousand(totalClick)}</div>,
          <div className="cell-value-table">{formatNumberInThousand(clickRate)}%</div>
        ]);
      });
    }
    return result;
  };

  handleSort = (headingIndex: number, direction: string) => {
    const { sortData } = this.props;
    console.log({
      headingIndex,
      direction,
      orderHeaders: orderHeaders[headingIndex],
      MAP_SORT_VALUE: MAP_SORT_VALUE[direction]
    });
    this.setState({
      directionSort: MAP_SORT_VALUE[direction]
    });

    sortData(
      'recommendationTopWidgetPerformance',
      orderHeaders[headingIndex],
      MAP_SORT_VALUE[direction]
    );
  };

  nextPage = () => {
    const { page, totalPage, recommendationTopWidgetPerformanceOrdered } = this.state;
    if (page === totalPage) {
      return;
    }

    this.paginateData(page + 1, recommendationTopWidgetPerformanceOrdered);
    this.setState({
      page: page + 1
    });
  };

  previousPage = () => {
    const { page, recommendationTopWidgetPerformanceOrdered } = this.state;
    if (page === 1) {
      return;
    }

    this.paginateData(page - 1, recommendationTopWidgetPerformanceOrdered);
    this.setState({
      page: page - 1
    });
  };

  getRecommendationTopWidget = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    const queryExecutionKey = QUERY_EXECUTION_KEY.RECOMMENDATION_TOP_WIDGET_PERFORMANCE;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getRecommendationTopWidget(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      TOP_ITEM_IN_DETAIL
    );
  };

  processExecutionId_RecommendationTopWidgetPerformance = (props, nextProps) => {
    const { recommendationTopWidgetPerformance_QueryExecutionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      recommendationTopWidgetPerformance_QueryExecutionId:
        recommendationTopWidgetPerformance_QueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(recommendationTopWidgetPerformance_QueryExecutionIdNext) &&
      recommendationTopWidgetPerformance_QueryExecutionId !==
        recommendationTopWidgetPerformance_QueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.RECOMMENDATION_TOP_WIDGET_PERFORMANCE,
        executionId: recommendationTopWidgetPerformance_QueryExecutionIdNext
      });
    }
  };

  updateCurrentKeySearch = currentKeySearch => {
    const { recommendationTopWidgetPerformance } = this.props;
    this.setState({ page: 1 });
    const dataFilteredAndSorted = filterWidgetBySearch(
      recommendationTopWidgetPerformance,
      currentKeySearch
    );

    this.setState({ currentKeySearch, data: dataFilteredAndSorted });
    if (!_.isEmpty(dataFilteredAndSorted)) {
      const newTotalPage = Math.ceil(
        dataFilteredAndSorted.length / NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL
      );
      this.setState({
        totalPage: newTotalPage
      });
    }

    this.paginateData(1, dataFilteredAndSorted);
  };

  render() {
    const {
      i18n,
      recommendationTopWidgetPerformanceLoading,
      openReviewAddonModal,
      performActionSurvey
    } = this.props;

    const {
      page,
      directionSort,
      totalPage,
      currentKeySearch,
      recommendationTopWidgetPerformanceOrdered
    } = this.state;
    const options = this.buildOptions();
    const arrowSort = directionSort === SORT_VALUE.ASCENDING ? CaretUpMinor : CaretDownMinor;

    const dataFilteredAndSorted = filterWidgetBySearch(
      recommendationTopWidgetPerformanceOrdered,
      currentKeySearch
    );

    return (
      <div
        className="BC-Page BC-Page__RecommendationAnalytics__Top__Widget"
        onScroll={this.handleScroll}>
        <Filter
          feature={FEATURES.RECOMMENDATION}
          performActionSurvey={performActionSurvey}
          fromDateStateName={fromDateStateName}
          toDateStateName={toDateStateName}
          compareStateName={compareStateName}
          metric={RECOMMENDATION_ANALYTIC_METRIC.TOP_WIDGET_PERFORMANCE}
          showCompare={false}
          openReviewAddonModal={openReviewAddonModal}
        />

        <div>
          <Card>
            <Card.Section>
              <div className="Polaris-Card__Header action-header">
                <Tooltip
                  preferredPosition="above"
                  content={i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendation_Tooltip_TopWidget_cardTitle'
                  )}>
                  <TextStyle>
                    <h2 className="Polaris-Heading">
                      {i18n.translate(
                        'AdminHTML.Analytics.RecommendationAnalytics.recommendationWidgetPerformance_cardTitle'
                      )}
                    </h2>
                  </TextStyle>
                </Tooltip>
                <TextField
                  onChange={value => this.updateCurrentKeySearch(value)}
                  value={currentKeySearch}
                  prefix={<Icon source={SearchMinor} color="base" />}
                  placeholder={i18n.translate('AdminHTML.Common.Action.search')}
                />
              </div>
            </Card.Section>
            <DataTable
              columnContentTypes={[
                'text',
                'text',
                'text',
                'text',
                'text',
                'text',
                'text',
                'text',
                'text'
              ]}
              sortable={[true, true, true, true, true, true, true, true, true]}
              onSort={this.handleSort}
              defaultSortDirection="descending"
              footerContent={
                _.isEmpty(dataFilteredAndSorted) && !recommendationTopWidgetPerformanceLoading
                  ? i18n.translate(
                      'AdminHTML.Analytics.RecommendationAnalytics.recommendationWidgetPerformance_noResult',
                      { count: 0 }
                    )
                  : ''
              }
              headings={[
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendationWidgetPerformance_header_widget_id'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendationWidgetPerformance_header_revenue'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendationWidgetPerformance_header_order_count'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendationWidgetPerformance_header_percent_item_order'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendationWidgetPerformance_header_conversion_rate'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendationWidgetPerformance_header_percent_item_cart'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendationWidgetPerformance_header_total_view'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendationWidgetPerformance_header_total_click'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendationWidgetPerformance_header_click_rate'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>
              ]}
              rows={options}
            />
          </Card>
          <div className="BC-Page__RecommendationAnalytics__Top__Widget__Pagination">
            {!_.isEmpty(dataFilteredAndSorted) && !recommendationTopWidgetPerformanceLoading && (
              <Pagination
                label={`${page}/${totalPage}`}
                hasPrevious={page > 1}
                onPrevious={this.previousPage}
                hasNext={page < totalPage}
                onNext={this.nextPage}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  changeValue: (name, value) => dispatch(AnalyticsActions.changeValue(name, value)),
  getRecommendationTopWidget: (fromDate, toDate, compare, queryExecutionId, top) =>
    dispatch(
      AnalyticsActions.getRecommendationTopWidget(
        fromDate,
        toDate,
        compare,
        queryExecutionId,
        top,
        ownProps.i18n
      )
    ),
  sortData: (name, field, direction, fieldInsensitive = '') =>
    dispatch(AnalyticsActions.sortData(name, field, direction, fieldInsensitive)),

  updateExecutionId: data => dispatch(AnalyticsQueryExecutionIdActions.updateExecutionId(data))
});

const mapStateToProps = (state, props) => {
  return {
    fromDate: _.get(state.analyticsV2, fromDateStateName),
    toDate: _.get(state.analyticsV2, toDateStateName),
    compare: _.get(state.analyticsV2, compareStateName, false),

    // Query execution ids
    executionIds: _.get(state.analyticsQueryExecutionId, 'list', []),

    // Collection: top
    recommendationTopWidgetPerformance: _.get(
      state.analyticsV2,
      'recommendationTopWidgetPerformance',
      []
    ),
    recommendationTopWidgetPerformanceLoading: _.get(
      state.analyticsV2,
      'recommendationTopWidgetPerformanceLoading',
      false
    ),
    recommendationTopWidgetPerformance_QueryExecutionId: _.get(
      state.analyticsV2,
      'recommendationTopWidgetPerformanceQueryExecutionId',
      ''
    )
  };
};

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(CollectionPerformanceAnalytics)
);
